import { useContext, useMemo } from 'react';
import { Navbar, Nav } from 'src/components/ui';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';

import { AuthContext } from '../context/AuthContext';
import RouterContext, { BreadcrumbItem } from '../context/RouterContext';
import { Logo } from './_general/Logo';
import Clock from './Clock';
import AppContext from '../context/AppContext';
import NavNotificationPanel from './NavNotificationPanel/NavNotificationPanel';
import PopupDesktopNotification from './PopupDesktopNotification';
import { DesktopNotificationPopupContext, DesktopNotificationPopupDispatchContext } from '../context/DesktopNotificationPopupContext';
import DropdownOrganizationSelected from './DropdownOrganizationSelected';
import CustomBadge from './CustomBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { getTestId } from '../hooks/useTestId';
import Icon from 'src/components/Icon';

const ENV_COLORS: Record<string, string> = {
  local: 'info',
  dev: 'primary',
  staging: 'danger',
  uat: '#9400D3',
  training: '#22b33d',
};

const EnvBadge = () => {
  const env = import.meta.env.VITE_APP_ENV ?? '';
  const color = ENV_COLORS[env];

  if (color == null) return null;

  return (
    <CustomBadge color={color} style={{ textTransform: 'uppercase', paddingLeft: '20px', paddingRight: '20px', fontSize: '0.8rem', flexShrink: 0 }}>
      {env}
    </CustomBadge>
  );
};

function NavMenu({ logoOnly, toggleSidebar, isCollapsed }: { logoOnly?: boolean; toggleSidebar?: (e: MouseEvent) => void; isCollapsed?: boolean }) {
  const auth = useContext(AuthContext);
  const { navbarContent } = useContext(AppContext);
  const navBreadcrumbs = useContext(RouterContext)?.navBreadcrumbs ?? [];

  const history = useHistory();
  const isMobile = useIsMobile();

  const desktopNotificationPopup = useContext(DesktopNotificationPopupContext);
  const desktopNotificationPopupDispatch = useContext(DesktopNotificationPopupDispatchContext);

  const navBarLeft = useMemo(() => {
    if (isMobile && isCollapsed) return '0px';
    if (isCollapsed) return '60px';
    return '280px';
  }, [isCollapsed, isMobile]);

  return auth.user ? (
    <div className="mui-main-content">
      <Navbar className="navbar-toggleable-sm" style={{ left: navBarLeft, zIndex: 1049, transition: 'left 0.3s ease' }} expand="sm" container={false}>
        {isMobile && isCollapsed && (
          <Link to="/">
            <Logo height={40} width="auto" />
          </Link>
        )}

        <button className="mui-toggle-btn" onClick={toggleSidebar as any} {...getTestId('nav', '.', 'sidebar-toggle')}>
          <Icon icon="List" />
        </button>

        {!logoOnly && (
          <>
            {/* Left: env badge + breadcrumb trail */}
            <div className="flex items-center gap-2 flex-grow ml-2 overflow-hidden">
              <EnvBadge />
              {navBreadcrumbs && navBreadcrumbs.length > 0 && (
                <nav
                  className="flex items-center flex-nowrap pr-5"
                  style={{ gap: '4px', fontSize: '0.9rem', minWidth: 0, flexDirection: 'row', display: 'flex' }}
                >
                  {navBreadcrumbs.map((crumb: BreadcrumbItem, i: number) => {
                    const isLast = i === navBreadcrumbs.length - 1;
                    const inner = crumb.icon ? <Icon icon={crumb.icon} /> : crumb.label;
                    return (
                      <span key={i} className="flex items-center" style={{ gap: '4px', flexShrink: isLast ? 1 : 0, minWidth: 0 }}>
                        {i > 0 && (
                          <span className="text-muted" style={{ opacity: 0.4, userSelect: 'none' }}>
                            <Icon icon="CaretRight" />
                          </span>
                        )}
                        {crumb.to ? (
                          <Link to={crumb.to} className="text-muted navbar-breadcrumb-link" style={{ textDecoration: 'none' }}>
                            {inner}
                          </Link>
                        ) : (
                          <span className="truncate" style={{ fontWeight: 500 }}>
                            {inner}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </nav>
              )}
            </div>

            {/* Right: clock (when collapsed) + injected content + org switcher + notifications */}
            <div className="flex items-center gap-2 flex-shrink-0 mr-2">
              {auth.user?.organizationalUnits?.length > 1 && <DropdownOrganizationSelected auth={auth} history={history} />}
              <NavNotificationPanel />
              {isCollapsed && (
                <span className="topbar-clock">
                  <Clock className="topbar-clock-time" />
                </span>
              )}
              {navbarContent && <Nav className="navbar-nav">{navbarContent}</Nav>}
            </div>
          </>
        )}
      </Navbar>

      {desktopNotificationPopup?.map(n => (
        <PopupDesktopNotification key={'popup-notif-' + n.id} notification={n} onClose={n => desktopNotificationPopupDispatch?.pop(n)} />
      ))}
    </div>
  ) : null;
}

export default NavMenu;
